// 导入相关模块和类型
import { bindAuthApi } from '@/api/common'
import { Language } from '@/utils/language'
import { useContext, useEffect } from 'react'
import { GOOGLE_CLIENT_ID, INVITE_CODE_STORAGE, SUCCESS_CODE } from '@/utils/constants'
import { BindAuthRes } from '@/types/api'
import { clearInviteCode, setToken } from '@/utils/checkLogin'
import useAfterOnboarding from './useAfterOnboarding'
import useAfterLogin from './useAfterLogin'
import { GlobalContext } from '@/context'
import useToken from './useToken'
import { useRouter } from 'next/router'

// Google One Tap 登录函数
function googleOneTap(
  {
    client_id,
    auto_select = false,
    cancel_on_tap_outside = false,
    context = 'signin',
    ...otherOptions
  }: {
    client_id: string
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
    context?: string
    [key: string]: any
  },
  callback: (response: any) => void
) {
  // 检查 client_id 是否存在
  if (!client_id) {
    throw new Error('client_id is required')
  }

  // 检查是否在浏览器环境中
  if (typeof window !== 'undefined' && window.document) {
    const contextValue = ['signin', 'signup', 'use'].includes(context) ? context : 'signin'
    const googleScript = document.createElement('script')
    googleScript.src = 'https://accounts.google.com/gsi/client'
    googleScript.async = true
    // googleScript.defer = true
    document.head.appendChild(googleScript)
    window.addEventListener('load', function () {
      if ((window as any).google) {
        ;(window as any).google.accounts.id.initialize({
          client_id: client_id,
          callback: callback,
          auto_select: auto_select,
          cancel_on_tap_outside: cancel_on_tap_outside,
          context: contextValue,
          ...otherOptions
        })
        ;(window as any).google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('notification getNotDisplayedReason', notification, notification.getNotDisplayedReason())
          }
        })
      }
    })
  }
}

// 定义 Token 类型
interface Token {
  raw: string
  header: any
  payload: any
}

// 解码 Base64Url
function decodeBase64Url(base64Url: string) {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}

// 解码 JWT 响应
function decodeJwtResponse(t?: string): Token {
  if (!t) return {} as Token

  const token: Token = {
    raw: '',
    header: {},
    payload: {}
  }

  token.raw = t
  token.header = JSON.parse(window.atob(t.split('.')[0]))
  token.payload = decodeBase64Url(t.split('.')[1])

  return token
}

// 使用 Google One Tap 登录的 Hook
const useGoogleOneTap = () => {
  const router = useRouter()
  const { query } = router
  const { f = '' } = query || {}
  const { isLogin } = useToken()
  const { lang } = useContext(GlobalContext)
  const { afterRegisterSuccess } = useAfterOnboarding({ rememberPassword: false })
  const { afterLoginSuccess } = useAfterLogin({ rememberPassword: false, noRedirect: true })

  // 绑定成功后的操作
  const onBindSuccess = (data: BindAuthRes) => {
    clearInviteCode()
    setToken(data?.access_token)
    window?.gtag?.('event', 'login', {
      entry: 'google'
    })

    if (!data?.user?.role) {
      afterRegisterSuccess()
    } else {
      afterLoginSuccess()
    }
  }

  // 绑定 Google 账号
  const bindGoogleAuth = (userInfo: any) => {
    const inviteCode = f || localStorage.getItem(INVITE_CODE_STORAGE) || ''

    bindAuthApi('google', {
      id: userInfo?.sub,
      nickname: userInfo?.name,
      name: userInfo?.name,
      email: userInfo?.email,
      avatar: userInfo?.picture,
      user: userInfo,
      invite_code: inviteCode as string
    }).then((bindRes) => {
      if (bindRes?.data?.code === SUCCESS_CODE) {
        onBindSuccess?.(bindRes?.data?.data)
      } else {
        //
      }
    })
  }

  // Google 回调函数
  const onBindGoogleCallback = (res: any) => {
    const decodeRes = decodeJwtResponse(res.credential)
    const userInfo = decodeRes?.payload
    bindGoogleAuth(userInfo)
  }

  // 使用 useEffect 进行初始化
  useEffect(() => {
    if (lang === Language.zhCN || isLogin) {
      return
    }

    googleOneTap({ client_id: GOOGLE_CLIENT_ID }, onBindGoogleCallback)
  }, [lang, isLogin])
}

export default useGoogleOneTap
